import {
  classifyPixel,
  type GeoRing,
  NEIGHBOR_COAST,
  NEIGHBOR_LAND,
  NL_LAND,
  NL_PROVINCES,
  NL_WATER,
  nlViewBbox3857,
  project,
  type RadarManifest,
  RIVERS,
  sampleIntensity,
} from '@puurweer/shared';

// project (projectie) + classifyPixel/sampleIntensity (radar-pixel → categorie) wonen sinds
// de baker-nowcast in @puurweer/shared; de island gebruikt ze hieronder.

/**
 * Radar-island (§7): de neerslag-nowcast "Komende 2 uur". Vervangt de SSR-mount door
 * een vaste NL-kaart (kustlijn her-geprojecteerd naar **EPSG:3857**), gestapelde
 * transparante KNMI-WMS-frames (via de eigen `/radar`-proxy → de key blijft server-side),
 * een locatiestip op de plaats (lat/lon → pixel via de bbox) en een antwoordzin uit de
 * échte radarpixels op die plek.
 *
 * Bediening à la KNMI, bewust zonder scrubber/legenda/knoppen (eigenaarskeuze: "geen
 * bloat"): de lus draait vanzelf, met alléén een meelopend klokje als overlay op de kaart.
 * Tikken op de kaart pauzeert/hervat; het klokje is een <button> en daarmee de
 * toetsenbord-/screenreader-toggle (WCAG 2.2.2). Bewust pauzeren schakelt elk
 * auto-hervatten uit. `prefers-reduced-motion`: **niet** auto-afspelen — de kaart start
 * stil op "nu"; bewust afspelen kan, vanzelf gebeurt er niets. Progressive enhancement:
 * lazy (pas zodra de sectie in beeld komt), no-op zonder de mount.
 */

/** Zwaartepunt van de neerslag in een frame (native px), gewogen naar intensiteit. */
export function computeCentroid(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): { x: number; y: number } | null {
  let sx = 0;
  let sy = 0;
  let sw = 0;
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const o = (y * width + x) * 4;
      const w = classifyPixel(data[o] ?? 0, data[o + 1] ?? 0, data[o + 2] ?? 0, data[o + 3] ?? 0);
      if (w > 0) {
        sx += x * w;
        sy += y * w;
        sw += w;
      }
    }
  }
  return sw < 6 ? null : { x: sx / sw, y: sy / sw };
}

const DIRS = [
  'noorden',
  'noordoosten',
  'oosten',
  'zuidoosten',
  'zuiden',
  'zuidwesten',
  'westen',
  'noordwesten',
] as const;

/** Verplaatsing (dx oost, dy zuid in canvas-px) → 8-punts windrichting waar het heen trekt. */
export function bearingToDutch(dx: number, dy: number): string {
  let a = (Math.atan2(dx, -dy) * 180) / Math.PI;
  if (a < 0) a += 360;
  return DIRS[Math.round(a / 45) % 8] ?? 'noorden';
}

/**
 * Het **overgangsframe** in de per-locatie-intensiteiten: regent het nu, dan de index waarop
 * het droog wordt; is het droog, dan de index waarop de regen begint. `null` als er geen
 * overgang is (de hele 2 uur droog of de hele 2 uur nat). Dezelfde index draagt de
 * antwoordzin én het stille streepje op de tijdlijn — die kunnen elkaar dus nooit tegenspreken.
 */
export function deriveRainMoment(intensities: readonly number[]): number | null {
  const WET = 1;
  if (intensities.length === 0) return null;
  if ((intensities[0] ?? 0) >= WET) {
    for (let i = 1; i < intensities.length; i++) {
      if ((intensities[i] ?? 0) < WET) return i;
    }
    return null; // regen de hele 2 uur
  }
  const start = intensities.findIndex((v) => v >= WET);
  return start > 0 ? start : null; // -1 = droog de hele 2 uur
}

/**
 * Antwoordzin uit de per-locatie-intensiteiten (mirror van de mock-logica): regent het
 * nu, wanneer wordt het droog, of wanneer begint het. `clock` formatteert een ISO-tijd.
 */
export function deriveRainAnswer(
  intensities: number[],
  times: string[],
  clock: (iso: string) => string,
): string {
  const WET = 1;
  if (intensities.length === 0) return 'Neerslagradar';
  const wetNow = (intensities[0] ?? 0) >= WET;
  const moment = deriveRainMoment(intensities);
  if (wetNow) {
    if (moment === null) return 'Regen de komende 2 uur';
    const t = times[moment];
    return t ? `Droog vanaf ${clock(t)}` : 'Regen de komende 2 uur';
  }
  if (moment === null) return 'Droog de komende 2 uur';
  const t = times[moment];
  return t ? `Regen rond ${clock(t)}` : 'Neerslag op komst';
}

/**
 * Steden als oriëntatiepunten op de kaart (à la Buienradar): zonder herkenningspunten is een
 * regenvlek moeilijk te plaatsen. Tier 1 = altijd; tier 2 alleen als de kaart breed genoeg
 * wordt weergegeven (anders wordt het smalle silhouet te druk). 'l' = label links van de stip
 * (kuststeden wijzen over zee; Leeuwarden weg van Groningen). lon/lat, publieke kennis.
 */
const CITIES: ReadonlyArray<
  readonly [name: string, lon: number, lat: number, tier: 1 | 2, align?: 'l']
> = [
  ['Amsterdam', 4.9, 52.37, 1],
  ['Rotterdam', 4.48, 51.92, 1],
  ['Den Haag', 4.3, 52.08, 2, 'l'],
  ['Utrecht', 5.12, 52.09, 1],
  ['Eindhoven', 5.48, 51.44, 1],
  ['Arnhem', 5.91, 51.98, 2],
  ['Enschede', 6.9, 52.22, 2],
  ['Zwolle', 6.09, 52.51, 2],
  ['Groningen', 6.57, 53.22, 1],
  ['Leeuwarden', 5.8, 53.2, 2, 'l'],
  ['Maastricht', 5.69, 50.85, 1],
  ['Middelburg', 3.61, 51.5, 2], // dicht bij de westrand → label rechts, anders valt 'ie buiten beeld
] as const;

/** Frame-tijd → klok HH:MM in Europe/Amsterdam. */
function clockAmsterdam(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', {
      timeZone: 'Europe/Amsterdam',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

/** Frame-index → minuten vooruit t.o.v. het eerste frame ("nu"), uit de échte frame-tijden. */
export function offsetMinutes(frames: RadarManifest['frames'], i: number): number {
  const t0 = frames[0]?.time;
  const ti = frames[i]?.time;
  if (!t0 || !ti) return i * 5; // val terug op vaste 5-min-stappen
  return Math.max(0, Math.round((Date.parse(ti) - Date.parse(t0)) / 60_000));
}

/** Minuten vooruit → kort relatief label: "nu" · "+45 min" · "+1 uur" · "+2 uur". */
export function relTime(min: number): string {
  if (min <= 0) return 'nu';
  if (min < 60) return `+${min} min`;
  return min % 60 === 0 ? `+${min / 60} uur` : `+${min} min`;
}

/**
 * aspect-ratio van het kaartvel — zelfde bron (NL-bbox) als de SSR-shell in radar-page.ts,
 * zodat ook de laad-/foutstaat exact de hoogte van de uiteindelijke kaart reserveert.
 */
function shellRatio(): string {
  const [minX, minY, maxX, maxY] = nlViewBbox3857();
  return `${Math.round(maxX - minX)} / ${Math.round(maxY - minY)}`;
}

/** De interactieve radar opbouwen + bedienen in de mount. */
function mountRadar(
  mount: HTMLElement,
  lat: number,
  lon: number,
  name: string,
  manifest: RadarManifest,
): void {
  const { bbox } = manifest;
  const frames = manifest.frames;
  if (frames.length === 0) {
    mount.innerHTML =
      '<p class="rain-answer">Neerslagradar</p><p class="hourly-cap">Geen radarbeelden beschikbaar.</p>';
    return;
  }

  // Kaart als het beeld, met alléén een meelopend klokje als overlay (à la KNMI) — geen
  // scrubber, geen losse bediening. Tikken op de kaart pauzeert/hervat de lus; het klokje
  // is een <button> en daarmee óók de toetsenbord-/screenreader-weg (WCAG 2.2.2: bewegende
  // content moet pauzeerbaar zijn). De antwoordzin is aria-live: verandert precies één keer
  // (laden → antwoord). De metaregel ("Beeld van … · bron KNMI") is een eerlijk manifest-feit.
  mount.innerHTML = `<p class="rain-answer pw-radar-loading" id="rain-answer" aria-live="polite">Neerslag van de komende twee uur</p>
<p class="radar-meta" id="radar-meta"></p>
<div class="canvas-wrap is-loading"><canvas id="radar" width="${bbox.width}" height="${bbox.height}" role="img" aria-label="Neerslagradar van Nederland met een markering op de gekozen plaats"></canvas><button type="button" class="radar-clock" id="radar-clock" aria-label="Pauzeer de radarlus" hidden><b id="clock-time"></b><span id="clock-rel"></span></button></div>
<p class="radar-drift" id="radar-drift" hidden></p>`;

  // Querien, dan narrowen naar non-null consts (zodat de closures hieronder ze
  // gegarandeerd kennen — TS draagt de guard niet vanzelf de closures in).
  const canvasNode = mount.querySelector<HTMLCanvasElement>('#radar');
  const clockBtnNode = mount.querySelector<HTMLButtonElement>('#radar-clock');
  const clockTimeNode = mount.querySelector<HTMLElement>('#clock-time');
  const clockRelNode = mount.querySelector<HTMLElement>('#clock-rel');
  const answerEl = mount.querySelector<HTMLElement>('#rain-answer');
  const metaEl = mount.querySelector<HTMLElement>('#radar-meta');
  const driftEl = mount.querySelector<HTMLElement>('#radar-drift');
  const wrapEl = mount.querySelector<HTMLElement>('.canvas-wrap');
  const ctx = canvasNode?.getContext('2d') ?? null;
  if (!canvasNode || !ctx || !clockBtnNode || !clockTimeNode || !clockRelNode) return;
  const canvas = canvasNode;
  const clockBtn = clockBtnNode;
  const clockTime = clockTimeNode;
  const clockRel = clockRelNode;

  // Het canvas-label noemt de gemarkeerde plaats — via setAttribute (nooit innerHTML:
  // de plaatsnaam is data). De metaregel: wanneer het radarbeeld is gemaakt + de bron.
  if (name) {
    canvas.setAttribute('aria-label', `Neerslagradar van Nederland met ${name} gemarkeerd`);
  }
  if (metaEl) {
    const t0 = frames[0]?.time;
    const clk0 = t0 ? clockAmsterdam(t0) : '';
    metaEl.textContent = clk0 ? `Beeld van ${clk0} · bron KNMI` : 'bron KNMI';
  }

  const darkMatch = matchMedia('(prefers-color-scheme: dark)');
  // prefers-reduced-motion: niet vanzelf afspelen (tik/klokje om bewust te starten).
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Verborgen sampler op WMS-native resolutie: leest de neerslagpixels per frame.
  const sampler = document.createElement('canvas');
  sampler.width = bbox.width;
  sampler.height = bbox.height;
  const sctx = sampler.getContext('2d', { willReadFrequently: true });

  // Statische basemap (zee/land/water/kustlijn): één keer per maat/thema naar een offscreen-canvas,
  // daarna per frame geblit i.p.v. de hele vectorkaart hertekenen → vloeiend afspelen.
  const base = document.createElement('canvas');
  const bctx = base.getContext('2d');
  // Stadslabels apart: die horen bóven de regen (anders onleesbaar onder een bui) maar zijn
  // net zo statisch — zelfde recept: één keer renderen, per frame blitten.
  const labels = document.createElement('canvas');
  const lctx = labels.getContext('2d');
  // Zachte "aquarel"-regen (zoals de goedgekeurde mock): het rauwe KNMI-frame eerst klein
  // (≈⅓) tekenen en dan bilineair terug opschalen — de harde paletcel-randen vervagen,
  // buien blijven gescheiden. Bewust géén ctx.filter-blur (Safari-inconsistent). De
  // sampler hieronder blijft de ráuwe frames lezen: antwoordzin/strook/drift kloppen exact.
  const soft = document.createElement('canvas');
  const softCtx = soft.getContext('2d');

  const images: HTMLImageElement[] = [];
  const intensities: number[] = new Array(frames.length).fill(0);
  let frame = 0; // huidige frame-index (geen scrubber meer die deze staat draagt)
  const [pinNx, pinNy] = project(lon, lat, bbox.bbox, bbox.width, bbox.height);

  // Bouwt één pad uit meerdere ringen (vasteland + eilanden, of de meren) — voor fill of stroke.
  function pathRings(
    c: CanvasRenderingContext2D,
    rings: ReadonlyArray<GeoRing>,
    w: number,
    h: number,
  ): void {
    c.beginPath();
    for (const ring of rings) {
      ring.forEach((pt, i) => {
        const [x, y] = project(pt[0], pt[1], bbox.bbox, w, h);
        if (i === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      });
      c.closePath();
    }
  }

  // Idem, maar voor open polylijnen (rivieren/grenzen/kustsegmenten): géén closePath.
  function pathLines(
    c: CanvasRenderingContext2D,
    lines: ReadonlyArray<GeoRing>,
    w: number,
    h: number,
  ): void {
    c.beginPath();
    for (const line of lines) {
      line.forEach((pt, i) => {
        const [x, y] = project(pt[0], pt[1], bbox.bbox, w, h);
        if (i === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      });
    }
  }

  /**
   * De vaste kaart als **topografisch blad** (à la KNMI/Buienradar, in kalme tinten):
   * de blauwe zee is de CSS-achtergrond van de `canvas-wrap` (loopt door over de volle
   * breedte); de canvas tekent op transparant de buurlanden (vergrijsd), NL (groen),
   * provinciegrenzen en rivieren. Binnenwater wordt transparant geponst zodat de wrap-zee
   * er naadloos doorheen schijnt. Eén keer naar de offscreen `base`.
   */
  function renderBase(w: number, h: number, dark: boolean): void {
    if (!bctx) return;
    base.width = w;
    base.height = h;
    const f = w / 560;
    const nlGreen = dark ? '#26331f' : '#d6e3c0'; // NL = zacht groen (het onderwerp)
    const buurland = dark ? '#222a2c' : '#e6e6da'; // buurland = vergrijsd, op de achtergrond
    const water = dark ? '#3c5a73' : '#8fb4cf'; // rivieren (zelfde familie als de wrap-zee)

    // 1. Buurland-vlakken (fill) + hun kust-/grenslijnen als losse polylijnen — de
    //    cliprand van de uitsnede wordt zo nooit per ongeluk als "kust" gestroked.
    pathRings(bctx, NEIGHBOR_LAND, w, h);
    bctx.fillStyle = buurland;
    bctx.fill();
    bctx.lineWidth = Math.max(1, w / 900);
    bctx.strokeStyle = dark ? '#39444a' : '#b6bcab';
    pathLines(bctx, NEIGHBOR_COAST, w, h);
    bctx.stroke();

    // 2. NL erbovenop; binnenwater (IJsselmeer e.a.) transparant ponsen → de blauwe wrap-zee.
    pathRings(bctx, NL_LAND, w, h);
    bctx.fillStyle = nlGreen;
    bctx.fill();
    bctx.save();
    bctx.globalCompositeOperation = 'destination-out';
    pathRings(bctx, NL_WATER, w, h);
    bctx.fill();
    bctx.restore();

    // 3. Rivieren — goed herkenbaar, in dezelfde blauwfamilie als de zee.
    bctx.lineWidth = Math.max(1, 1.2 * f);
    bctx.strokeStyle = water;
    bctx.lineJoin = 'round';
    bctx.lineCap = 'round';
    pathLines(bctx, RIVERS, w, h);
    bctx.stroke();

    // 4. Provinciegrenzen — haarfijn, net zichtbaar.
    bctx.lineWidth = Math.max(0.6, 0.7 * f);
    bctx.strokeStyle = dark ? 'rgba(150, 168, 180, 0.28)' : 'rgba(90, 105, 95, 0.3)';
    pathLines(bctx, NL_PROVINCES, w, h);
    bctx.stroke();

    // 5. NL-kust en meeroevers als duidelijke rand (boven de rivieren/grenzen).
    bctx.lineWidth = Math.max(1.2, w / 700);
    bctx.strokeStyle = dark ? '#46555f' : '#8da394';
    pathRings(bctx, NL_LAND, w, h);
    bctx.stroke();
    pathRings(bctx, NL_WATER, w, h);
    bctx.stroke();
  }

  /**
   * Stadslabels (oriëntatiepunten) naar de offscreen `labels`-laag — boven de regen geblit,
   * met een papieren halo zodat ze óók op een bui leesbaar blijven. `displayW` (CSS-pixels)
   * bepaalt hoeveel labels passen; `scale` = bitmap/CSS (dpr) houdt het lettertype op maat.
   */
  function renderLabels(w: number, h: number, dark: boolean, displayW: number): void {
    if (!lctx) return;
    labels.width = w;
    labels.height = h;
    const scale = displayW > 0 ? w / displayW : 1;
    // Tier 2 pas op een breed blad: op de brede uitsnede is NL relatief kleiner,
    // dus op mobiel (alles ≲ 480 CSS-px) alleen de zes grote steden — geen gedrum.
    const showAll = displayW >= 480;
    const fontPx = Math.round(13.5 * scale);
    // Sans uit de stack (Hanken Grotesk), iets groter voor leesbaarheid; witte letters met een
    // strakke 1px zwarte rand — leesbaar op land, zee én een bui (in beide modi). De webfonts
    // staan op de pagina.
    lctx.font = `600 ${fontPx}px "Hanken Grotesk", system-ui, -apple-system, sans-serif`;
    lctx.textBaseline = 'middle';
    lctx.lineJoin = 'round';
    const dot = dark ? '#9db0bd' : '#49574e';
    const labelFill = '#fff';
    const labelBorder = '#000'; // strakke, volledig zwarte rand om de witte letters
    const [pinX, pinY] = project(lon, lat, bbox.bbox, w, h);
    for (const [name, clon, clat, tier, align] of CITIES) {
      if (tier === 2 && !showAll) continue;
      const [x, y] = project(clon, clat, bbox.bbox, w, h);
      // De gekozen plaats heeft al een pin — een stadslabel er pal naast is dubbelop.
      if (Math.hypot(x - pinX, y - pinY) < 22 * scale) continue;
      lctx.beginPath();
      lctx.arc(x, y, 1.9 * scale, 0, 7);
      lctx.fillStyle = dot;
      lctx.fill();
      // Links van de stip bij een 'l'-hint of tegen de rechterrand; anders rechts.
      const left = align === 'l' || x > w * 0.78;
      lctx.textAlign = left ? 'right' : 'left';
      const tx = left ? x - 4.5 * scale : x + 4.5 * scale;
      lctx.strokeStyle = labelBorder;
      // Gecentreerde stroke; de fill dekt de binnenhelft → ~1px zwart zichtbaar rondom de letter.
      lctx.lineWidth = 2 * scale;
      lctx.strokeText(name, tx, y);
      lctx.fillStyle = labelFill;
      lctx.fillText(name, tx, y);
    }
  }

  function drawPin(w: number, h: number, dark: boolean): void {
    if (!ctx) return;
    const [px, py] = project(lon, lat, bbox.bbox, w, h);
    const f = w / 560; // schaalfactor
    // Warm accent (zelfde familie als de locatie-pin in de kop): leesbaar op groen land,
    // blauwe zee én blauwe regen — een blauwe stip verdronk in de topografische kaart.
    const body = dark ? '#d98a72' : '#b35b4a';
    const casing = dark ? '#0f1722' : '#fff';

    // Moderne locatiestip, exact gecentreerd op de plaats (de oude druppel-pin hing vóór en
    // onder het punt en oogde gedateerd): kern met een witte ring — vlak en grafisch, géén
    // halo of slagschaduw. Een dun donker randje geeft definitie op elke ondergrond.
    ctx.beginPath();
    ctx.arc(px, py, 5.2 * f, 0, 7);
    ctx.fillStyle = body;
    ctx.fill();
    ctx.lineWidth = 2.4 * f;
    ctx.strokeStyle = casing;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(px, py, 6.6 * f, 0, 7);
    ctx.lineWidth = 0.9 * f;
    ctx.strokeStyle = dark ? 'rgba(231, 236, 242, 0.3)' : 'rgba(12, 43, 71, 0.3)';
    ctx.stroke();

    // De plaatsnaam bij de stip — de plaats is hét punt van deze kaart, dus die verdient een
    // naam. Zelfde recept als de stadslabels (sans, witte letters met een strakke 1px zwarte rand,
    // leesbaar óók op een bui), nét wat groter; tegen de oostrand spiegelt het label naar links.
    if (name) {
      const scale = canvas.clientWidth > 0 ? w / canvas.clientWidth : 1;
      ctx.font = `600 ${Math.round(15 * scale)}px "Hanken Grotesk", system-ui, -apple-system, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      const left = px > w * 0.72;
      ctx.textAlign = left ? 'right' : 'left';
      const tx = left ? px - 11 * f : px + 11 * f;
      ctx.strokeStyle = '#000';
      // Gecentreerde stroke; de fill dekt de binnenhelft → ~1px zwart zichtbaar rondom de letter.
      ctx.lineWidth = 2 * scale;
      ctx.strokeText(name, tx, py);
      ctx.fillStyle = '#fff';
      ctx.fillText(name, tx, py);
    }
  }

  function draw(i: number): void {
    if (!ctx) return;
    frame = i;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (base.width > 0) ctx.drawImage(base, 0, 0, w, h); // gecachete basemap (1 blit)
    const img = images[i]; // radarbeeld over land én water (niet geclipt → buien op zee zichtbaar)
    if (img?.complete && img.naturalWidth > 0) {
      if (softCtx && soft.width > 0) {
        // klein → groot = zachte aquarel-regen (zie de soft-canvas hierboven)
        softCtx.clearRect(0, 0, soft.width, soft.height);
        softCtx.drawImage(img, 0, 0, soft.width, soft.height);
        ctx.drawImage(soft, 0, 0, w, h);
      } else {
        ctx.drawImage(img, 0, 0, w, h);
      }
    }
    if (labels.width > 0) ctx.drawImage(labels, 0, 0, w, h); // stadslabels bóven de regen
    drawPin(w, h, darkMatch.matches);
    // Het meelopende klokje op de kaart: kloktijd leidend ("16:25"), de tijd vooruit
    // zachter ernaast (" · +75 min") — concreet eerst, context erbij.
    const rel = relTime(offsetMinutes(frames, i));
    const t = frames[i]?.time;
    const clk = t ? clockAmsterdam(t) : '';
    clockTime.textContent = clk || rel;
    clockRel.textContent = clk ? ` · ${rel}` : '';
  }

  function fit(): void {
    const cssW = canvas.clientWidth;
    if (!cssW) return;
    const w = Math.round(cssW * Math.min(2, devicePixelRatio || 1));
    // Mobiel scrollen klapt de adresbalk in/uit en vuurt resize-events af zónder dat de
    // kaart van maat verandert — dan niets doen. Zonder deze guard ontstond bovendien een
    // terugkoppellus (bitmap-attributen ↔ lay-outbreedte) die de kaart liet "zoomen".
    // (`base.width > 0` = er is al eens gerenderd; de allereerste fit slaat nooit over.)
    if (w === canvas.width && base.width > 0) return;
    // De lay-out hangt aan CSS (aspect-ratio op het element), niet aan de bitmap-attributen:
    // de attributen hieronder wijzigen alleen de tekenresolutie, nooit de getoonde maat.
    canvas.style.aspectRatio = `${bbox.width} / ${bbox.height}`;
    canvas.width = w;
    canvas.height = Math.round((w * bbox.height) / bbox.width);
    // canvas.width/height zetten reset de context → smoothing-hint hierna (scherper opschalen).
    if (ctx) ctx.imageSmoothingQuality = 'high';
    // De verzachtings-tussenmaat (≈⅓ van de canvas ≈ 2–3 km op NL-schaal) schaalt mee.
    soft.width = Math.max(64, Math.round(w / 3));
    soft.height = Math.max(64, Math.round((soft.width * bbox.height) / bbox.width));
    if (softCtx) softCtx.imageSmoothingQuality = 'high';
    renderBase(w, canvas.height, darkMatch.matches); // basemap één keer per maat
    renderLabels(w, canvas.height, darkMatch.matches, cssW); // stadslaag idem
    draw(frame);
  }

  function sampleFrame(i: number, img: HTMLImageElement): void {
    if (!sctx) return;
    try {
      sctx.clearRect(0, 0, sampler.width, sampler.height);
      sctx.drawImage(img, 0, 0, sampler.width, sampler.height);
      const patch = sctx.getImageData(
        Math.max(0, Math.round(pinNx) - 3),
        Math.max(0, Math.round(pinNy) - 3),
        7,
        7,
      );
      intensities[i] = sampleIntensity(patch.data, patch.width, patch.height, 3, 3);
    } catch {
      // sampling mislukt (zou niet mogen, same-origin) → laat 0 staan
    }
  }

  function centroidOfFrame(i: number): { x: number; y: number } | null {
    const img = images[i];
    if (!sctx || !img?.complete || img.naturalWidth === 0) return null;
    try {
      sctx.clearRect(0, 0, sampler.width, sampler.height);
      sctx.drawImage(img, 0, 0, sampler.width, sampler.height);
      const full = sctx.getImageData(0, 0, sampler.width, sampler.height);
      return computeCentroid(full.data, full.width, full.height);
    } catch {
      return null;
    }
  }

  function finalize(): void {
    wrapEl?.classList.remove('is-loading'); // vangnet (bv. als het "nu"-frame zelf faalde)
    if (!reduced && !userPaused) play(); // meestal al gestart tijdens 't laden; veiligheidsnet
    // Antwoordzin: de gedempte laadregel valt weg (CSS-fade) en wordt het echte antwoord — één
    // rustige reveal i.p.v. een harde tekstsprong vanuit een placeholder-zin.
    if (answerEl) {
      answerEl.classList.remove('pw-radar-loading');
      answerEl.textContent = deriveRainAnswer(
        intensities,
        frames.map((f) => f.time),
        clockAmsterdam,
      );
    }
    // Drift: alleen als het bij de pin regent (mock-conventie) + genoeg verplaatsing.
    if (driftEl) {
      const wet = intensities.flatMap((v, i) => (v >= 1 ? [i] : []));
      const drift =
        wet.length >= 2 ? driftBetween(wet[0] as number, wet[wet.length - 1] as number) : null;
      if (drift) {
        driftEl.hidden = false;
        driftEl.textContent = `Buien trekken naar het ${drift}.`;
      } else {
        driftEl.hidden = true;
      }
    }
  }

  function driftBetween(a: number, b: number): string | null {
    const ca = centroidOfFrame(a);
    const cb = centroidOfFrame(b);
    if (!ca || !cb) return null;
    const dx = cb.x - ca.x;
    const dy = cb.y - ca.y;
    if (Math.hypot(dx, dy) < bbox.width * 0.02) return null; // te weinig beweging
    return bearingToDutch(dx, dy);
  }

  // ── Frames laden + DECODEN. Het "nu"-frame verschijnt zodra dát binnen is; de autoplay start al
  //    zodra de eerste paar aaneengesloten frames klaar zijn en het lus-venster groeit mee terwijl
  //    de rest laadt (géén teller of voortgang — stil laden). Pas als álles binnen is zetten we de
  //    antwoordzin/drift (eerlijk: geen half antwoord). ──
  let ready = 0; // aantal aaneengesloten frames (vanaf "nu") dat al gedecodeerd is
  let userPaused = false; // bewust gepauzeerd (tik/klokje) → autoplay/vangnet blijven uit
  const arrived = new Array<boolean>(frames.length).fill(false);
  const MIN_FRAMES_TO_PLAY = 3; // start de autoplay zodra dit aantal frames klaar is
  async function loadFrame(i: number, url: string): Promise<void> {
    const img = new Image();
    images[i] = img;
    img.src = url;
    try {
      await img.decode(); // wacht tot 't beeld zonder hitch te tekenen is (geen blank/flikker)
      sampleFrame(i, img);
      if (i === frame) {
        draw(i); // toon "nu" zodra die binnen is
        wrapEl?.classList.remove('is-loading'); // eerste echte beeld → shimmer weg
        clockBtn.hidden = false; // het klokje verschijnt mét het eerste echte beeld
      }
    } catch {
      // load/decode mislukt → frame blijft leeg; de basemap blijft staan
    }
    arrived[i] = true;
    while (ready < frames.length && arrived[ready]) ready++; // schuif het aaneengesloten venster op
    if (!reduced && !userPaused && ready >= MIN_FRAMES_TO_PLAY) play(); // de lus groeit mee
    if (ready === frames.length) finalize(); // alles binnen → antwoordzin/drift
  }
  frames.forEach((f, i) => void loadFrame(i, f.url));

  // ── Afspelen (vanzelf, in een lus, ~KNMI-tempo). Vlot door de frames; alleen op het laatste
  // frame (+2 u) even *vasthouden* — niet op "nu" — dan rustig terug naar het begin. Tikken op
  // de kaart pauzeert/hervat; het klokje (een <button>) is dezelfde toggle voor toetsenbord en
  // screenreader. Bewust pauzeren zet `userPaused` → niets hervat vanzelf meer. Met
  // `prefers-reduced-motion` start de kaart stil op "nu": bewust afspelen kan (tik/klokje),
  // vanzelf gebeurt er niets. ──
  const STEP_MS = 500; // rustig tempo: een halve seconde per frame (5 min) — geen flits-loop
  const HOLD_MS = 1500; // freeze van 1,5 s, alléén op het laatste frame (+2 u)
  let raf: number | null = null;
  let last = 0;
  function setPlaying(on: boolean): void {
    clockBtn.classList.toggle('is-paused', !on);
    clockBtn.setAttribute('aria-label', on ? 'Pauzeer de radarlus' : 'Speel de radarlus af');
  }
  function loop(now: number): void {
    if (ready < 2) {
      raf = requestAnimationFrame(loop); // nog te weinig frames om te animeren → blijf wachten
      return;
    }
    const full = ready >= frames.length;
    // vasthouden op het laatste frame (+2 u) pas als álles binnen is; tijdens 't groeien vlot door
    const wait = full && frame === frames.length - 1 ? HOLD_MS : STEP_MS;
    if (now - last >= wait) {
      last = now;
      draw((frame + 1) % ready); // loop binnen de al-geladen frames; het venster groeit mee
    }
    raf = requestAnimationFrame(loop);
  }
  function play(): void {
    if (raf !== null) return; // speelt al
    last = 0; // direct doorstappen bij start (geen dode beat vóór de eerste stap)
    raf = requestAnimationFrame(loop);
    setPlaying(true);
  }
  function pause(): void {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    setPlaying(false);
  }
  // Tik op de kaart = pauze/verder (één listener op de wrap; een klik op het klokje borrelt
  // hierheen op, dus ook Enter/spatie op het klokje schakelt de lus).
  wrapEl?.addEventListener('click', () => {
    if (raf !== null) {
      userPaused = true; // bewuste keuze → niets hervat vanzelf
      pause();
    } else {
      userPaused = false;
      play();
    }
  });
  darkMatch.addEventListener?.('change', () => {
    renderBase(canvas.width, canvas.height, darkMatch.matches);
    renderLabels(canvas.width, canvas.height, darkMatch.matches, canvas.clientWidth);
    draw(frame);
  });
  addEventListener('resize', fit);

  fit();
  // De label-webfont (Hanken Grotesk) kan ná de eerste render binnenkomen → labels één keer opnieuw.
  document.fonts?.ready
    .then(() => {
      renderLabels(canvas.width, canvas.height, darkMatch.matches, canvas.clientWidth);
      draw(frame);
    })
    .catch(() => {});
}

export function initRadar(): void {
  const mount = document.getElementById('radar-mount');
  if (!mount) return;
  const lat = Number(mount.dataset.lat);
  const lon = Number(mount.dataset.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
  const name = mount.dataset.name ?? '';

  // Kalme foutstaat op het lege kaartvel. 503 = radar bewust uit (retry is zinloos);
  // anders een netwerk-/serverhik mét "Probeer opnieuw". Copy en knop zijn vaste,
  // interne strings — geen externe data in deze innerHTML.
  let started = false;
  function showError(kind: 'uit' | 'hik'): void {
    const head = kind === 'uit' ? 'De radar staat even uit' : 'De radar doet het even niet';
    const cap =
      kind === 'uit'
        ? 'De neerslagradar is tijdelijk niet beschikbaar. Probeer het later nog eens.'
        : 'Het radarbeeld kon niet laden.';
    const retry =
      kind === 'hik' ? '<button type="button" class="pw-radar-retry">Probeer opnieuw</button>' : '';
    (mount as HTMLElement).innerHTML = `<p class="rain-answer">${head}</p>
<div class="canvas-wrap pw-radar-shell" style="--radar-ratio: ${shellRatio()}"><div><p class="pw-radar-cap">${cap}</p>${retry}</div></div>`;
    if (kind === 'hik') {
      started = false; // opnieuw proberen mag
      mount?.querySelector('.pw-radar-retry')?.addEventListener('click', () => void load());
    }
  }

  // Throttle voor de visibilitychange-refetch: niet vaker verversen dan de radar-bake-cadans (5 min).
  const REFRESH_MIN_MS = 5 * 60_000;
  let lastLoadAt = 0;
  let refreshing = false;

  /** Haal `/radar` op en (her)bouw de kaart. Geeft de status terug zodat de aanroeper beslist of
   *  een foutstaat getoond wordt (eerste load) of het bestaande beeld blijft staan (refresh). */
  async function fetchAndMount(): Promise<'ok' | 'uit' | 'hik'> {
    try {
      const res = await fetch('/radar');
      if (!res.ok) return res.status === 503 ? 'uit' : 'hik';
      const manifest = (await res.json()) as RadarManifest;
      lastLoadAt = Date.now();
      mountRadar(mount as HTMLElement, lat, lon, name, manifest);
      return 'ok';
    } catch {
      return 'hik'; // netwerk weg of ongeldig antwoord
    }
  }

  async function load(): Promise<void> {
    if (started) return;
    started = true;
    const status = await fetchAndMount();
    if (status !== 'ok') showError(status); // rustige melding + (bij 'hik') retry
  }

  /** Tab komt weer in beeld → ververs het radarbeeld: de radar-bake levert elke 5 min een nieuw
   *  manifest, maar de island fetcht 't normaal alleen bij het laden. Rustig: alleen als de radar al
   *  geladen is en het laatste beeld ouder is dan de bake-cadans; géén polling, géén origin-call
   *  (Bunny). Een hik laat het huidige beeld gewoon staan (geen foutstaat over een werkend beeld). */
  async function refresh(): Promise<void> {
    if (refreshing || !lastLoadAt || Date.now() - lastLoadAt < REFRESH_MIN_MS) return;
    refreshing = true;
    await fetchAndMount();
    refreshing = false;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });

  // De radar is een gewone, altijd zichtbare sectie (geen <details> meer). Lazy: pas laden
  // zodra de sectie in beeld scrolt (scheelt een KNMI-call als de bezoeker nooit zover komt).
  // Zonder IntersectionObserver: meteen laden.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            io.disconnect();
            void load();
          }
        }
      },
      { rootMargin: '250px' },
    );
    io.observe(mount);
  } else {
    void load();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRadar);
} else {
  initRadar();
}
